import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Checkbox, FormControlLabel, Typography, Box, Stack } from '@mui/material/';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';

import { CssTextField } from '../../../components/others/CssTextField';
import { formatDate } from '../../../services/functions';

export const FilterTree = React.forwardRef((props, ref) => {
  const { season } = useSelector(state => state.app);
  const { data, initSelected } = props;
  const [selected, setSelected] = React.useState([]);
  const [birthDateFrom, setBirthDateFrom] = React.useState(null);
  const [birthDateTo, setBirthDateTo] = React.useState(null);
  const [reservationDateFrom, setReservationDateFrom] = React.useState(null);
  const [reservationDateTo, setReservationDateTo] = React.useState(null);
  const [periodDateFrom, setPeriodDateFrom] = React.useState(null);
  const [periodDateTo, setPeriodDateTo] = React.useState(null);
  const [agreementSigningDateFrom, setAgreementSigningDateFrom] = React.useState(null);
  const [agreementSigningDateTo, setAgreementSigningDateTo] = React.useState(null);
  const [advancePaymentDateFrom, setAdvancePaymentDateFrom] = React.useState(null);
  const [advancePaymentDateTo, setAdvancePaymentDateTo] = React.useState(null);
  const [finalPaymentDateFrom, setFinalPaymentDateFrom] = React.useState(null);
  const [finalPaymentDateTo, setFinalPaymentDateTo] = React.useState(null);


  const resetFilters = () => [
    { birth_date_from: null },
    { birth_date_to: null },
    { reservation_date_from: null },
    { reservation_date_to: null },
    { period_date_to: null },
    { period_date_from: null },
    { agreement_signing_date_from: null },
    { agreement_signing_date_to: null },
    { advance_payment_date_from: null },
    { advance_payment_date_to: null },
    { final_payment_date_from: null },
    { final_payment_date_to: null }
  ];

  React.useImperativeHandle(ref, () => ({ getFilters: () => [
    ...selected,
    { birth_date_from: birthDateFrom },
    { birth_date_to: birthDateTo },
    { reservation_date_from: reservationDateFrom },
    { reservation_date_to: reservationDateTo },
    { period_date_to: periodDateTo },
    { period_date_from: periodDateFrom },
    { agreement_signing_date_from: agreementSigningDateFrom },
    { agreement_signing_date_to: agreementSigningDateTo },
    { advance_payment_date_from: advancePaymentDateFrom },
    { advance_payment_date_to: advancePaymentDateTo },
    { final_payment_date_from: finalPaymentDateFrom },
    { final_payment_date_to: finalPaymentDateTo }
  ], resetFilters }));

  React.useEffect(() => {
    if (initSelected === undefined) {
      setSelected([]);
      setBirthDateFrom(null);
      setBirthDateTo(null);
      setReservationDateFrom(null);
      setReservationDateTo(null);
      setPeriodDateFrom(null);
      setPeriodDateTo(null);
      setAgreementSigningDateFrom(null);
      setAgreementSigningDateTo(null);
      setAdvancePaymentDateFrom(null);
      setAdvancePaymentDateTo(null);
      setFinalPaymentDateFrom(null);
      setFinalPaymentDateTo(null);
    }
    if (!_.isEmpty(initSelected)) {
      initSelected.filter(el => {
        if (el.birth_date_from !== undefined) {
          setBirthDateFrom(el.birth_date_from);
        }
        if (el.birth_date_to !== undefined) {
          setBirthDateTo(el.birth_date_to);
        }

        if (el.reservation_date_from !== undefined) {
          setReservationDateFrom(el.reservation_date_from);
        }
        if (el.reservation_date_to !== undefined) {
          setReservationDateTo(el.reservation_date_to);
        }

        if (el.period_date_from !== undefined) {
          setPeriodDateFrom(el.period_date_from);
        }
        if (el.period_date_to !== undefined) {
          setPeriodDateTo(el.period_date_to);
        }
        if (el.agreement_signing_date_from !== undefined) {
          setAgreementSigningDateFrom(el.agreement_signing_date_from);
        }
        if (el.agreement_signing_date_to !== undefined) {
          setAgreementSigningDateTo(el.agreement_signing_date_to);
        }
        if (el.advance_payment_date_from !== undefined) {
          setAdvancePaymentDateFrom(el.advance_payment_date_from);
        }
        if (el.advance_payment_date_to !== undefined) {
          setAdvancePaymentDateTo(el.advance_payment_date_to);
        }
        if (el.final_payment_date_from !== undefined) {
          setFinalPaymentDateFrom(el.final_payment_date_from);
        }
        if (el.final_payment_date_to !== undefined) {
          setFinalPaymentDateTo(el.final_payment_date_to);
        }

        return true;
      });
      setSelected(initSelected.filter(el => typeof (el) != 'object'));
    }
  }, [season, initSelected]);
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const parentMap = React.useMemo(() => goThroughAllNodes(data), []);

  function goThroughAllNodes(nodes, map = {}) {
    if (!nodes.children) {
      return null;
    }

    map[nodes.id] = getAllChild(nodes).splice(1);

    for (const childNode of nodes.children) {
      goThroughAllNodes(childNode, map);
    }

    return map;
  }

  // Get all children from the current node.
  function getAllChild(childNode, collectedNodes = []) {
    if (childNode === null) {
      return collectedNodes;
    }

    collectedNodes.push(childNode.id);

    if (Array.isArray(childNode.children)) {
      for (const node of childNode.children) {
        getAllChild(node, collectedNodes);
      }
    }

    return collectedNodes;
  }

  const getChildById = (nodes, id) => {
    const array = [];
    const path = [];

    // recursive DFS
    function getNodeById(node, id, parentsPath) {
      let result = null;

      if (node.id === id) {
        return node;
      } else if (Array.isArray(node.children)) {
        for (const childNode of node.children) {
          result = getNodeById(childNode, id, parentsPath);

          if (Boolean(result)) {
            parentsPath.push(node.id);

            return result;
          }
        }

        return result;
      }

      return result;
    }

    const nodeToToggle = getNodeById(nodes, id, path);

    return { childNodesToToggle: getAllChild(nodeToToggle, array), path };
  };

  function getOnChange(checked, nodes) {
    const { childNodesToToggle, path } = getChildById(data, nodes.id);

    let array = checked
      ? [...selected, ...childNodesToToggle]
      : selected
        .filter(value => !childNodesToToggle.includes(value))
        .filter(value => !path.includes(value));

    array = array.filter((v, i) => array.indexOf(v) === i);

    setSelected(array);
  }

  const renderTree = nodes => {
    const allSelectedChildren = parentMap[
      nodes.id
    ]?.every(childNodeId => selectedSet.has(childNodeId));
    const checked = selectedSet.has(nodes.id) || allSelectedChildren || false;

    const indeterminate =
      parentMap[nodes.id]?.some(childNodeId => selectedSet.has(childNodeId)
      ) || false;

    if (allSelectedChildren && !selectedSet.has(nodes.id)) {
      setSelected([...selected, nodes.id]);
    }

    return (
      <Box key={nodes.id}>
        { nodes.id.includes('daterange')
          ? <TreeItem nodeId={nodes.id} label={<Typography style={{ padding: 8 }}>{nodes.name}</Typography>}>
            <LocalizationProvider dateAdapter={AdapterDateFns} >
              {nodes.id.includes('birth_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="birth_date__from"
                    label="Od"
                    value={birthDateFrom}
                    onChange={val => setBirthDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="birth_date__to"
                    label="Do"
                    value={birthDateTo}
                    onChange={val => setBirthDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
              {nodes.id.includes('reservation_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="reservation_date__from"
                    label="Od"
                    value={reservationDateFrom}
                    onChange={val => setReservationDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="reservation_date__to"
                    label="Do"
                    value={reservationDateTo}
                    onChange={val => setReservationDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
              {nodes.id.includes('period_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="period_date__from"
                    label="Od"
                    value={periodDateFrom}
                    onChange={val => setPeriodDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="period_date__to"
                    label="Do"
                    value={periodDateTo}
                    onChange={val => setPeriodDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
              {nodes.id.includes('agreement_signing_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="agreement_signing_date__from"
                    label="Od"
                    value={agreementSigningDateFrom}
                    onChange={val => setAgreementSigningDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="agreement_signing_date__to"
                    label="Do"
                    value={agreementSigningDateTo}
                    onChange={val => setAgreementSigningDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
              {nodes.id.includes('advance_payment_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="advance_payment_date__from"
                    label="Od"
                    value={advancePaymentDateFrom}
                    onChange={val => setAdvancePaymentDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="advance_payment_date__to"
                    label="Do"
                    value={advancePaymentDateTo}
                    onChange={val => setAdvancePaymentDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
              {nodes.id.includes('final_payment_date')
                ? <Stack direction="column" alignItems="center" spacing={1} sx={{ mr: 4, pb: 1, pt: 1 }}>
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="final_payment_date__from"
                    label="Od"
                    value={finalPaymentDateFrom}
                    onChange={val => setFinalPaymentDateFrom(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                  {/* &nbsp;- */}
                  <DatePicker
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    autoOk
                    name="final_payment_date__to"
                    label="Do"
                    value={finalPaymentDateTo}
                    onChange={val => setFinalPaymentDateTo(formatDate(val))}
                    renderInput={props => <CssTextField variant="standard" {...props} />}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Stack> : null}
            </LocalizationProvider>
          </TreeItem>
          : <TreeItem
            key={nodes.id}
            nodeId={nodes.id}
            label={ nodes.id == 'all'
              ? <Typography sx={{ padding: 1 }}>Filtry</Typography>
              : <FormControlLabel
                sx={!_.isNil(nodes.color) ? { color: nodes.color } : {}}
                control={
                  <Checkbox
                    sx={!_.isNil(nodes.color)
                      ? {
                        color: nodes.color,
                        '&.Mui-checked': {
                          color: nodes.color
                        }
                      }
                      : {}}
                    checked={checked}
                    indeterminate={!checked && indeterminate}
                    onChange={event => getOnChange(event.currentTarget.checked, nodes)
                    }
                    onClick={e => e.stopPropagation()}
                  />
                }
                label={<>{nodes.name}</>}
                key={nodes.id}
              />
            }
          >
            {Array.isArray(nodes.children)
              ? nodes.children.map(node => renderTree(node))
              : null}
          </TreeItem>
        }
      </Box>
    );
  };

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={['all']}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultEndIcon={null}
    >
      {renderTree(data)}
    </TreeView>
  );
});
